import { getBearerToken } from "@risalabs_frontend_org/auth-wrapper";
import { ApiError } from "./errors";

type HttpMethod = "GET" | "POST" | "PUT" | "PATCH" | "DELETE";

async function request<TRes>(
  method: HttpMethod,
  url: string,
  body?: unknown,
  signal?: AbortSignal,
): Promise<TRes> {
  const token = await getBearerToken();
  if (!token) throw new Error("Failed to retrieve bearer token");

  const multipart = typeof FormData !== "undefined" && body instanceof FormData;
  const response = await fetch(url, {
    method,
    headers: {
      ...(!multipart && { "Content-Type": "application/json" }),
      Authorization: `Bearer ${token}`,
    },
    ...(body !== undefined && { body: multipart ? (body as FormData) : JSON.stringify(body) }),
    signal,
  });

  if (!response.ok) {
    const errorBody = await response.json().catch(() => ({}));
    throw new ApiError(response.status, response.statusText, errorBody);
  }

  return response.json() as Promise<TRes>;
}

/**
 * Authenticated API client.
 *
 * Usage:
 *   import { api } from "@risalabs_frontend_org/api-client";
 *   const data = await api.post<Req, Res>(url, body, signal);
 *   const data = await api.get<Res>(url, signal);
 */
export const api = {
  get: <TRes>(url: string, signal?: AbortSignal) =>
    request<TRes>("GET", url, undefined, signal),

  post: <TReq, TRes>(url: string, body: TReq, signal?: AbortSignal) =>
    request<TRes>("POST", url, body, signal),

  put: <TReq, TRes>(url: string, body: TReq, signal?: AbortSignal) =>
    request<TRes>("PUT", url, body, signal),

  patch: <TReq, TRes>(url: string, body: TReq, signal?: AbortSignal) =>
    request<TRes>("PATCH", url, body, signal),

  delete: <TRes>(url: string, signal?: AbortSignal) =>
    request<TRes>("DELETE", url, undefined, signal),
};
